import { useIntl } from 'react-intl';
import { useReadAiChatFigureRowsQuery } from 'store/api/aiChatFigureApi';
import type { FigureCell, FigureDefinition, FigureRowsColumn } from 'types/aiChatFigure';
import { cn } from 'utils/cn';
import { FigureCellValue } from './FigureCellValue';
import { FigureFrame } from './FigureFrame';
import { columnLabel, figureTile } from './figureMeta';
import { FigureReveal, FigureStatus } from './RowsFigure';
import { RecordChips } from './FigureTable';

// One column in the panel and on the phone, two from 32rem: the page's column. Its 16px gutter is the key numbers',
// so in the 360 view the second column starts under the third key number.
const FIELDS_CLASS_NAME = 'm-0 grid gap-x-4 gap-y-3 @lg:grid-cols-2';

// A chip in a field keeps to its column: a long name truncates there, its number kept.
const FIELD_CHIP_CLASS_NAME = 'max-w-full';

// Several chips sit on the value's line as one chip does: a field's label above and the gap below keep the chips'
// targets apart, so the room a table cell keeps around them is not needed here.
const FIELD_CHIPS_CLASS_NAME = 'py-0 pointer-coarse:py-0';

// Chips that wrap keep their lines 14px apart (26px under a coarse pointer) for their targets: the next field starts
// farther down than that, so the lines read as one field.
const CHIPS_FIELD_CLASS_NAME = 'pb-1 pointer-coarse:pb-4';

const isEmpty = (cell: FigureCell | undefined) =>
    cell === undefined ||
    cell === null ||
    (typeof cell === 'string' && !cell.trim()) ||
    (Array.isArray(cell) && cell.length === 0);

interface FieldsProps {
    columns: FigureRowsColumn[];
    cells: FigureCell[];
    className?: string;
}

/**
 * A record's fields as label and value pairs, each value drawn as a Figure cell reads: a record as its chip, several
 * as wrapping chips, a date in the firm's format. A field with no value is left out.
 */
export function RecordFields({ columns, cells, className }: FieldsProps) {
    const fields = columns.flatMap((column, index) => (isEmpty(cells[index]) ? [] : [{ column, cell: cells[index] }]));
    if (!fields.length) {
        return null;
    }
    return (
        <dl className={cn(FIELDS_CLASS_NAME, className)}>
            {fields.map(({ column, cell }) => (
                <div
                    key={column.field}
                    className={cn('flex min-w-0 flex-col gap-0.5', Array.isArray(cell) && CHIPS_FIELD_CLASS_NAME)}
                >
                    <dt className="text-caption text-foreground-tertiary">{columnLabel(column)}</dt>
                    <dd className="text-foreground m-0 min-w-0 wrap-break-word">
                        {Array.isArray(cell) ? (
                            <RecordChips
                                cells={cell}
                                column={column}
                                chipClassName={FIELD_CHIP_CLASS_NAME}
                                className={FIELD_CHIPS_CLASS_NAME}
                            />
                        ) : (
                            <FigureCellValue cell={cell} column={column} chipClassName={FIELD_CHIP_CLASS_NAME} />
                        )}
                    </dd>
                </div>
            ))}
        </dl>
    );
}

const BODY_CLASS_NAME = 'flex flex-col items-start gap-3 px-4 pb-4';

/** The bars while the fields load: a label over a value for each field asked for, in the fields' grid. */
export function RecordFieldsSkeleton({ fields }: { fields: number }) {
    return (
        <div className={cn(FIELDS_CLASS_NAME, 'w-full')}>
            {Array.from({ length: fields }, (_, index) => (
                <div key={index} className="flex flex-col gap-1.5 py-0.5">
                    <div className="bg-muted h-3 w-20 rounded-sm" />
                    <div className="bg-muted h-4 w-40 max-w-full rounded-sm" />
                </div>
            ))}
        </div>
    );
}

/** The bars while the record loads: its chip, then its fields'. */
function RecordSkeleton({ fields }: { fields: number }) {
    return (
        <>
            <div className="bg-muted h-6 w-56 max-w-full rounded-md" />
            <RecordFieldsSkeleton fields={fields} />
        </>
    );
}

/**
 * A record card: one contact, case or colleague, read again for the viewer by its source, in the Figure frame with
 * the source's tile and the title. Its body is the record's own chip, then the fields the answer asked for. It counts
 * nothing and offers no Download or full size: there is one record.
 */
export function RecordFigure({ definition }: { definition: FigureDefinition }) {
    const intl = useIntl();
    const query = useReadAiChatFigureRowsQuery(definition, { skip: !definition.source });
    const rows = query.data;
    const tile = figureTile(definition);
    const loading = !rows && !query.isError;
    const row = rows?.rows[0];

    let body = <FigureStatus definition={definition} empty={false} query={query} />;
    if (rows && !row) {
        body = (
            <p className="text-meta text-foreground-secondary px-4 pb-4">
                {intl.formatMessage({ id: 'ai-chat.figure.record.gone' })}
            </p>
        );
    } else if (rows && row) {
        // The runner puts the record itself first; the fields the answer asked for follow.
        body = (
            <div className={BODY_CLASS_NAME}>
                <FigureCellValue cell={row[0]} column={rows.columns[0]} />
                <RecordFields columns={rows.columns.slice(1)} cells={row.slice(1)} className="w-full" />
            </div>
        );
    }

    return (
        <FigureFrame
            icon={tile.icon}
            tileClassName={tile.className}
            title={definition.title}
            busy={loading}
            className="my-3"
        >
            {/* Keyed by the error, so a retry shows the bars again. */}
            <FigureReveal
                key={query.isError ? 'error' : 'rows'}
                loading={loading}
                skeleton={<RecordSkeleton fields={definition.columns?.length ?? 4} />}
                skeletonClassName={cn(BODY_CLASS_NAME, 'pt-1')}
            >
                {body}
            </FigureReveal>
        </FigureFrame>
    );
}
